import {
  StyleSheet,
  Dimensions,
  Text,
  View,
  TouchableOpacity,
  Linking,
  Platform,
} from "react-native";
import { BarCodeScanner } from "expo-barcode-scanner";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import PageTitle from "./PageTitle";
import { useIsFocused, useFocusEffect } from "@react-navigation/native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import IconButton from "./ButtonIcon";
import * as Colors from "../styles/colors";
import * as Fonts from "../styles/typography";
import { Toast } from "react-native-toast-message/lib/src/Toast";
import { PaymentDataVirtualStamp } from "../services/Api/virtualstamp";
import { GlobalContext } from "../contexts/global";
import { IS_WEB } from "@services/Config";

const { width, height } = Dimensions.get("screen");

export default function ScanQRCodeVirtualStamp({ navigation }) {
  const [hasPermission, setHasPermission] = useState(null);
  const { setLoading, setShowAlert, showAlert } = useContext(GlobalContext);
  const [type, setType] = useState(BarCodeScanner.Constants.Type.back);
  const [scanned, setScanned] = useState(false);
  const isFocused = useIsFocused();
  
  // Web-specific state
  const [webError, setWebError] = useState(null);
  const [webData, setWebData] = useState(null);
  const [isWebScannerActive, setIsWebScannerActive] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scannerRef = useRef(null);

  // Use useFocusEffect to handle focus/blur events more reliably
  useFocusEffect(
    useCallback(() => {
      console.log("Screen focused, initializing camera");
      // This runs when the screen comes into focus
      if (IS_WEB && hasPermission) {
        // Make sure to stop any existing scanner first
        if (scannerRef.current) {
          stopWebScanner();
        }
        // Small delay to ensure previous resources are cleaned up
        setTimeout(() => {
          initializeWebScanner();
        }, 100);
      }
      
      // This cleanup function runs when the screen goes out of focus
      return () => {
        if (IS_WEB) {
          console.log("Cleaning up camera resources on blur");
          stopWebScanner();
        }
      };
    }, [hasPermission])
  );

  // Keep the original useEffect for initial permission request
  useEffect(() => {
    (async () => {
      try {
        if (IS_WEB) {
          // For web, we'll just set permission to true since we'll handle it differently
          setHasPermission(true);
        } else {
          const { status } = await BarCodeScanner.requestPermissionsAsync();
          setHasPermission(status === "granted");
        }
      } catch (error) {
        console.error("Error requesting camera permission:", error);
        setHasPermission(false);
      }
    })();
  }, []);

  // Handle web barcode scanning
  useEffect(() => {
    if (IS_WEB && webData) {
      handleBarCodeScanned({ type: 'unknown', data: webData });
      setWebData(null); // Reset after processing
    }
  }, [webData]);

  // Keep the unmount effect as an additional safety measure
  useEffect(() => {
    return () => {
      if (IS_WEB) {
        console.log("Component unmounting, cleaning up camera");
        stopWebScanner();
      }
    };
  }, []);

  const initializeWebScanner = async () => {
    try {
      console.log("Initializing web scanner");
      if (!videoRef.current) {
        console.log("Video refs not available");
        return;
      }

      if (!canvasRef.current) {
        console.log("canvas refs not available");
        return;
      }
      
      // Make sure any existing stream is stopped
      if (videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
      
      // Use a simpler approach for web scanning
      // We'll use the browser's built-in capabilities
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      // Set up a simple interval to check for QR codes
      const interval = setInterval(() => {
        if (videoRef.current && canvasRef.current) {
          const context = canvasRef.current.getContext('2d');
          canvasRef.current.width = videoRef.current.videoWidth;
          canvasRef.current.height = videoRef.current.videoHeight;
          context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
          
          // For now, we'll just show the camera feed without scanning
          // In a real implementation, you would use a library like jsQR here
          // to detect QR codes in the canvas
        }
      }, 100);
      
      scannerRef.current = interval;
      setIsWebScannerActive(true);
      console.log("Web scanner initialized successfully");
    } catch (error) {
      console.error('Error initializing web scanner:', error);
      setWebError(error);
    }
  };

  const stopWebScanner = () => {
    console.log("Stopping web scanner");
    if (scannerRef.current) {
      try {
        clearInterval(scannerRef.current);
        scannerRef.current = null;
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
    
    if (videoRef.current && videoRef.current.srcObject) {
      try {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          track.stop();
          videoRef.current.srcObject.removeTrack(track);
        });
        videoRef.current.srcObject = null;
      } catch (error) {
        console.error('Error stopping camera tracks:', error);
      }
    }
    
    setIsWebScannerActive(false);
    console.log("Web scanner stopped");
  };

  const handleBarCodeScanned = (scanningResult) => {
    let data;
    
    if(IS_WEB){
      const { type, data: scannedData } = scanningResult;
      data = scannedData;
    } else {
      const { type, data: scannedData, bounds: { origin } = {} } = scanningResult;
      data = scannedData;
    }

    setScanned(true);
    (async () => {
      setLoading({ show: true, message: "Carregando informações do ticket" })
      PaymentDataVirtualStamp(data)
        .then((value) => {
          let vehicleInSpace = value.vehicleInSpace
          if(vehicleInSpace) {
            setShowAlert({
              type: "success",
              title: "Atenção",
              message: "Ticket carimbado.",
              show: true,
              confirmText: "OK",
              pressConfirm: () => {
                setShowAlert({ ...showAlert, show: false });
                navigation.goBack();
              },
            });
          } else {
            setShowAlert({
              type: "info",
              title: "Atenção",
              message: "Ticket carimbado - não há vagas",
              show: true,
              confirmText: "OK",
              pressConfirm: () => {
                setShowAlert({ ...showAlert, show: false });
                navigation.goBack();
              },
            });
          }
        })
        .catch((error) => {
          setShowAlert({
            type: "alert",
            title: "Atenção",
            message: (error.message
              ?? error.erro)
              ?? "Ocorreu um erro ao tentar carimbar o ticket",
            show: true,
            confirmText: "OK",
            pressConfirm: () => {
              setShowAlert({ ...showAlert, show: false });
              navigation.goBack();
            },
          });
        })
        .finally(() => {
          setLoading({ show: false, message: "" })
        }
        );
    })();
  };

  if (hasPermission === null) {
    return <Text>Pedindo permissão para acessar a camera</Text>;
  }
  if (hasPermission === false) {
    return (
      <PageTitle title={"ESCANEIE O QR CODE DO CLIENTE"} navigation={navigation}>
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            flexGrow: 1,
          }}
        >
          <Icon name="camera-off" size={128} />
          <Text style={{ margin: 24, fontSize: Fonts.FONT_SIZE_16 }}>
            {"Sem permissão para a camera"}
          </Text>
          <IconButton
            iconName={"camera-outline"}
            textoBotao={"Permitir"}
            estilo={{ backgroundColor: Colors.WARNING }}
            onPress={() => {
              if (!IS_WEB) {
                BarCodeScanner.requestPermissionsAsync().then((value) => {
                  if(!value.canAskAgain){
                    Toast.show({
                      type: 'error',
                      text1: 'Permissão negada pelo sistema',
                      text2: 'Clique aqui para ver as configurações',
                      topOffset: 90,
                      onPress: () => Linking.openSettings()
                    });
                  }
                  setHasPermission(value.granted);
                });
              } else {
                setHasPermission(true);
              }
            }}
          />
        </View>
      </PageTitle>
    );
  }
  return (
    <PageTitle title={"ESCANEIE O QR CODE DO CLIENTE"} navigation={navigation}>
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        {isFocused ? (
          !IS_WEB ? (
            <BarCodeScanner
              onBarCodeScanned={handleBarCodeScanned}
              type={type}
              barCodeTypes={[BarCodeScanner.Constants.BarCodeType.qr, BarCodeScanner.Constants.BarCodeType.code128]}
              style={{
                width: width / 1.25,
                height: height / 1.50,
              }}
            />
          ) : (
            <View style={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              {webError ? (
                <>
                  <Text style={{ marginBottom: 20, fontSize: Fonts.FONT_SIZE_16, color: Colors.ALERT }}>
                    Erro ao acessar a câmera: {webError.message}
                  </Text>
                  <IconButton
                    iconName={"qrcode-scan"}
                    textoBotao={"Tentar Novamente"}
                    estilo={{ backgroundColor: Colors.SECONDARY }}
                    onPress={() => {
                      setWebError(null);
                      initializeWebScanner();
                    }}
                  />
                </>
              ) : (
                <View style={{ width: width / 1.25, height: height / 1.50, overflow: 'hidden', position: 'relative' }}>
                  <video 
                    ref={videoRef}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      display: isWebScannerActive ? 'block' : 'none'
                    }}
                  />
                  <canvas 
                    ref={canvasRef}
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'none'
                    }}
                  />
                  {!isWebScannerActive && (
                    <View style={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      bottom: 0, 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.1)'
                    }}>
                      <Text style={{ fontSize: Fonts.FONT_SIZE_16, marginBottom: 20 }}>
                        Iniciando câmera...
                      </Text>
                    </View>
                  )}
                </View>
              )}
            </View>
          )
        ) : null}
      </View>
    </PageTitle>
  );
}

const styles = StyleSheet.create({
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  separator: {
    marginVertical: 30,
    height: 1,
    width: "80%",
  },
});
